// Modo offline: caché de lecturas clave, cola de ventas y sincronización automática.
const CACHE_PREFIX = "off_cache:";
const QUEUE_KEY = "off_sales_queue";
const SEQ_KEY = "off_sales_seq";

const CACHEABLE = [/^\/?auth\/me$/, /^\/?config$/, /^\/?products(\?.*)?$/, /^\/?customers$/, /^\/?accounts$/, /^\/?cash\/current$/,
  /^\/?promotions(\?.*)?$/, /^\/?point\/credentials$/, /^\/?categories$/, /^\/?held-sales$/, /^\/?platform\/version$/, /^\/?license\/status$/];

const state = { online: typeof navigator === "undefined" ? true : navigator.onLine, syncing: false, pending: 0, lastError: "" };
const listeners = new Set();
function emit() { state.pending = readQueue().length; listeners.forEach((cb) => { try { cb({ ...state }); } catch { /* noop */ } }); }
export function onOffline(cb) { listeners.add(cb); cb({ ...state, pending: readQueue().length }); return () => listeners.delete(cb); }
export const offlineState = () => ({ ...state, pending: readQueue().length });
export const isOnline = () => state.online;

const norm = (url = "") => url.replace(/^https?:\/\/[^/]+\/api/, "").replace(/^\//, "");
export const isCacheable = (url) => CACHEABLE.some((rx) => rx.test(norm(url)));

export function cacheSet(url, data) {
  try { localStorage.setItem(CACHE_PREFIX + norm(url), JSON.stringify({ t: Date.now(), data })); } catch { /* cuota llena: ignorar */ }
}
export function cacheGet(url) {
  try { const raw = localStorage.getItem(CACHE_PREFIX + norm(url)); return raw ? JSON.parse(raw).data : undefined; } catch { return undefined; }
}
const cachedProducts = () => cacheGet("products") || [];

// Búsqueda offline por código de barras (producto o variante)
export function offlineBarcode(code) {
  const list = cachedProducts();
  let p = list.find((x) => x.barcode === code);
  if (p) return p;
  p = list.find((x) => (x.variants || []).some((v) => v.barcode === code));
  if (!p) return null;
  const v = p.variants.find((x) => x.barcode === code);
  return { ...p, _scanned_variant: v?.name || "" };
}
export function offlineSearch(q) {
  const t = (q || "").toLowerCase();
  return cachedProducts().filter((p) => (p.name || "").toLowerCase().includes(t) || (p.barcode || "").includes(t) || (p.sku || "").toLowerCase().includes(t)).slice(0, 50);
}

// ---- Cola de ventas ----
function readQueue() { try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); } catch { return []; } }
function writeQueue(q) { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); emit(); }
export const pendingSales = () => readQueue();
export function discardPending(clientId) { writeQueue(readQueue().filter((x) => x.client_id !== clientId)); }

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Guarda la venta localmente y devuelve un objeto de venta "sintético" para ticket/UI
export function queueSale(payload) {
  const seq = Number(localStorage.getItem(SEQ_KEY) || "0") + 1;
  localStorage.setItem(SEQ_KEY, String(seq));
  const client_id = payload.client_id || (window.crypto?.randomUUID?.() || `off-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const me = cacheGet("auth/me") || {};
  const total = r2((payload.items || []).reduce((a, it) => a + Number(it.qty || 0) * Number(it.price || 0), 0));
  const favor = r2(payload.favor_used || 0);
  const payTotal = r2(total - favor);
  let change = 0;
  if (payload.payment_method === "efectivo") change = Math.max(0, r2((payload.cash_received || 0) - payTotal));
  if (payload.payment_method === "mixto") {
    const nonCash = r2((payload.payments || []).filter((p) => p.method !== "efectivo").reduce((a, p) => a + Number(p.amount || 0), 0));
    change = Math.max(0, r2((payload.cash_received || 0) - (payTotal - nonCash)));
  }
  const offline_at = new Date().toISOString();
  const body = { ...payload, client_id, offline_at };
  const sale = {
    id: client_id, client_id, folio: `OFF-${seq}`, offline: true, items: payload.items, total, subtotal: total, iva: 0,
    payment_method: payload.payment_method, cash_received: payload.cash_received || 0, change, payments: payload.payments || [],
    customer_id: payload.customer_id || "", customer_name: payload.customer_name || "", cashier_name: me.name || "", date: offline_at,
    is_credit: payload.payment_method === "credito", paid: payload.payment_method === "credito" ? 0 : total, comment: payload.comment || "",
    is_whatsapp: !!payload.is_whatsapp, whatsapp_name: payload.whatsapp_name || "", favor_used: favor, status: "completed",
  };
  writeQueue([...readQueue(), { client_id, body, sale, created_at: offline_at, error: "" }]);
  // Descontar stock local para que el inventario/POS reflejen la venta
  const prods = cachedProducts();
  let touched = false;
  (payload.items || []).forEach((it) => {
    const p = prods.find((x) => x.id === it.product_id);
    if (!p || p.non_inventoriable) return;
    touched = true;
    p.stock = r2((p.stock || 0) - Number(it.qty || 0));
    if (it.variant && p.has_variants) { const v = (p.variants || []).find((x) => x.name === it.variant); if (v) v.stock = r2((v.stock || 0) - Number(it.qty || 0)); }
  });
  if (touched) cacheSet("products", prods);
  return sale;
}

// ---- Sincronización ----
let apiRef = null;
let timer = null;
export function initOffline(api) {
  apiRef = api;
  const setOnline = (v) => { state.online = v; emit(); if (v) syncPending(); };
  window.addEventListener("online", () => setOnline(true));
  window.addEventListener("offline", () => setOnline(false));
  if (!timer) timer = setInterval(() => { if (navigator.onLine && readQueue().length) syncPending(); }, 30000);
  if (navigator.onLine && readQueue().length) setTimeout(syncPending, 1500);
  emit();
}

export async function syncPending() {
  if (!apiRef || state.syncing) return;
  const q = readQueue();
  if (!q.length) return;
  state.syncing = true; state.lastError = ""; emit();
  try {
    for (const item of q) {
      try {
        await apiRef.post("/sales", item.body, { __skipOffline: true });
        writeQueue(readQueue().filter((x) => x.client_id !== item.client_id));
      } catch (e) {
        if (!e.response) { state.online = false; break; } // sin red: reintentar después
        const detail = e.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : JSON.stringify(detail || e.message);
        writeQueue(readQueue().map((x) => x.client_id === item.client_id ? { ...x, error: msg } : x));
        state.lastError = msg;
      }
    }
  } finally {
    state.syncing = false;
    if (navigator.onLine) state.online = true;
    emit();
    if (!readQueue().length) window.dispatchEvent(new CustomEvent("offline-synced"));
  }
}
